import React from 'react';
import { MemoryRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import LegalDocumentPage from './pages/LegalDocumentPage';

/**
 * LEGAL-PUBLIC-CANONICAL-URL-02 — canonical, hash-free public legal URL.
 *
 * ONE public URL per legal document: https://treatx.health/legal/<key> is what
 * the user sees in the address bar, what we hand to Meta, and what crawlers
 * fetch. The main app is a HashRouter SPA, so rendering this route through it
 * would always rewrite the address bar to `#/legal/<key>`. Instead this shell
 * mounts the SAME `LegalDocumentPage` inside a MemoryRouter: routing state
 * lives in memory, so the browser URL is never touched.
 *
 * Content still comes exclusively from Agreement Management via the existing
 * public resolver (LegalDocumentPage → CMSContent → useContent →
 * GET /api/public/content/resolve): current published version only, canonical
 * EN/TR locale + fallback rules. No legal text, key list or version is
 * hard-coded here, and no second legal SSOT is introduced.
 *
 * Legacy `#/legal/<key>` URLs keep working through the main app's route.
 */
export const PUBLIC_LEGAL_PATH_RE = /^\/legal\/([a-z0-9_-]+)\/?$/i;

export function matchPublicLegalPath(pathname) {
  const m = PUBLIC_LEGAL_PATH_RE.exec(pathname || '');
  return m ? m[1] : null;
}

export default function PublicLegalShell({ contentKey }) {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <MemoryRouter initialEntries={[`/legal/${contentKey}`]}>
          <Routes>
            <Route path="/legal/:contentKey" element={<LegalDocumentPage />} />
          </Routes>
        </MemoryRouter>
      </LanguageProvider>
    </HelmetProvider>
  );
}
